/** biome-ignore-all lint/style/useNamingConvention: camel case */
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {isMealOptionsEventType} from '~/common/utils/event_utils';
import {Button} from '~library/atoms/Button';
import {Radio} from '~library/atoms/Radio';
import {Typography} from '~library/atoms/Typography';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {CheckmarkCircleIcon} from '~library/svg/CheckmarkCircleIcon';
import {DeleteBinLineIcon} from '~library/svg/DeleteBinLineIcon';
import {EditLineIcon} from '~library/svg/EditLineIcon';
import {MealOptionsIcon} from '~library/svg/MealOptionsIcon';
import {PollLineIcon} from '~library/svg/PollLineIcon';
import {emerald, largishAndUp, link} from '~libSass/base/_exports.sass';
import {PollChoiceOverlay} from '../../common/PollChoiceOverlay';
import {voteForPollOption} from '../../reducers/apiActions';
import {isPreview} from '../../utils';
import {trackPollVote} from '../../utils/analytics';

export const PollModule = (props) => {
  const {restored, onPropagateRestore, readonly} = props;
  const {options, question, pollId, optionSelectedByUser, voted, hasRSVPed} = useSelector(
    ({poll: p, rsvp}) => ({
      options: p.options?.map((o) => ({
        key: o.id,
        choice: o.option,
        response_count: o.response_count,
      })),
      question: p.question,
      allowOtherOptions: p.allow_other_options,
      totalResponseCount: p.total_response_count,
      pollId: p.id,
      voted: p.voted,
      optionSelectedByUser: p.option_selected_by_user,
      hasRSVPed: rsvp.hasRsvpd,
    })
  );

  const isHost = useSelector((state) => state.user?.isHost);
  const eventId = useSelector((state) => state.eventDetails?.eventId);
  const widgets = useSelector((state) => state.eventDetails?.widgets);
  const eventType = useSelector((state) => state.eventDetails?.eventType);
  const showMealOptions = isMealOptionsEventType(eventType);

  const [selectedChoice, setSelectedChoice] = useState(false);
  const [changingChoice, setChangingChoice] = useState(false);
  const pollOverlayChoice = '';
  const [pollOverlayOpen, setPollOverlayOpen] = useState(false);
  const guestsWhoVotedThis = [];
  const {matches: largeScreens} = useMatchQuery(`(min-width: ${largishAndUp})`);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!optionSelectedByUser) {
      setChangingChoice(true);
    }
    setSelectedChoice(optionSelectedByUser);
  }, [optionSelectedByUser]);

  const submitPollVote = () => {
    trackPollVote('polls_vote');
    dispatch(
      voteForPollOption(pollId, 'cast_vote', {option: selectedChoice}, () => {
        setChangingChoice(false);
      })
    );
  };

  const changeChoice = () => {
    setChangingChoice(true);
  };

  const preview = isPreview();

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (restored && preview && isHost) {
      // API call to restore module
      const patchedEventData = {
        widgets,
      };
      onPropagateRestore(patchedEventData);
    }
  }, [restored]);

  return (
    <div className="view-invitation__poll-module">
      {preview && !readonly && (
        <div className="view-invitation__preview-cta multi">
          <a
            className="view-invitation__preview-cta"
            href={`/invitation/${eventId}/details`}
            data-qa-id="previewCtaEdit"
            ref={(elem) => {
              if (props.connectCTA) props.connectCTA('edit-poll', elem);
            }}
          >
            <EditLineIcon ratio={0.5} color={link} />
            Edit
          </a>
          <span>|</span>
          <button
            type="button"
            className="unstyled-button view-invitation__preview-cta"
            ref={(elem) => props.connectCTA && props.connectCTA('remove-poll', elem)}
            data-qa-id="previewCtaRemove"
          >
            <DeleteBinLineIcon ratio={0.5} color={link} />
            Remove
          </button>
        </div>
      )}
      <div className="view-invitation__poll-module__main-contents">
        <div className="view-invitation__title-with-icon-wrapper">
          {showMealOptions ? <MealOptionsIcon ratio={0.75} /> : <PollLineIcon ratio={0.75} />}
          <Typography
            variant="header3"
            data-qa-id={showMealOptions ? 'meal-options-module-title' : 'poll-module-title'}
          >
            {showMealOptions ? 'Meal options' : 'Poll'}
          </Typography>
        </div>
        <Typography
          variant="paragraph3"
          color="pebble"
          classNames="view-invitation__poll-module__poll-question"
          data-qa-id="poll-question"
        >
          {question}
        </Typography>

        {voted && !changingChoice
          ? <div
              className="view-invitation__poll-module__poll-result-grid"
              data-qa-id="poll-module-guest"
            >
              {options?.map((o, i) => (
                <div
                  className={`view-invitation__poll-module__poll-result ${
                    o.key === optionSelectedByUser ? 'winner' : ''
                  }`}
                  key={`pollOption_${o.key}`}
                >
                  <div className="view-invitation__poll-module__poll-result-name">
                    <Typography variant="paragraph2" data-qa-id="poll-option" data-qa-value={o.key}>
                      {o.choice}
                    </Typography>
                    {o.key === optionSelectedByUser && (
                      <CheckmarkCircleIcon ratio={0.75} color={emerald} />
                    )}
                  </div>
                  <div className="view-invitation__poll-module__results-view">
                    <Typography
                      variant="paragraph2"
                      classNames="view-invitation__poll-module__result-count"
                      data-qa-id="poll-option-response-count"
                      data-qa-value={o.response_count}
                    >
                      {o.response_count}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          : <div className="view-invitation__poll-module__poll-options">
              {options?.map((o, i) => (
                <Radio
                  onChange={() => setSelectedChoice(o.key)}
                  data-qa-id="poll-option"
                  data-qa-value={o.key}
                  checked={selectedChoice !== undefined && selectedChoice === o.key}
                  label={o.choice}
                  id={`pollOption_${o.key}`}
                  key={`${o.key}_${i}`}
                  name={`pollOption_${o.key}`}
                  disabled={preview || !hasRSVPed}
                />
              ))}
            </div>}
      </div>
      <div className="view-invitation__poll-module__vote-cta-wrapper">
        <Button
          data-qa-id={changingChoice ? 'submit-choice' : 'change-my-choice'}
          variant="primary"
          size={largeScreens ? 'large' : 'small'}
          onClick={changingChoice ? submitPollVote : changeChoice}
          noTextWrap
          disabled={preview || !hasRSVPed || !selectedChoice}
        >
          {changingChoice ? 'Submit Choice' : 'Change My Choice'}
        </Button>
        {!preview && !hasRSVPed && (
          <Typography variant="paragraph3" color="pebble">
            You have to RSVP to vote.
          </Typography>
        )}
      </div>
      <PollChoiceOverlay
        choice={pollOverlayChoice}
        open={pollOverlayOpen}
        onClose={() => setPollOverlayOpen(false)}
        guests={guestsWhoVotedThis}
      />
    </div>
  );
};
